// Push Notification Service for HDC PWA
const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to get auth header
const getAuthHeader = () => {
  const token = localStorage.getItem('hyperdriveToken');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

// Convert VAPID public key from base64 to Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// Check if push notifications are supported
export function isPushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window;
}

// Check if notifications are enabled
export function isNotificationEnabled() {
  return Notification.permission === 'granted';
}

// Request notification permission
export async function requestNotificationPermission() {
  if (!isPushSupported()) {
    console.log('Push notifications not supported');
    return false;
  }
  
  const permission = await Notification.requestPermission();
  return permission === 'granted';
}

// Subscribe to push notifications
export async function subscribeToPush() {
  if (!isPushSupported()) {
    throw new Error('Push notifications not supported');
  }
  
  // Wait for service worker to be ready
  const registration = await navigator.serviceWorker.ready;
  
  // Check if already subscribed
  let subscription = await registration.pushManager.getSubscription();
  
  if (subscription) {
    console.log('Already subscribed to push notifications');
    return subscription;
  }
  
  // Get VAPID public key from server
  const response = await fetch(`${API_URL}/api/push/vapid-public-key`);
  if (!response.ok) {
    throw new Error('Failed to get VAPID public key');
  }
  const { publicKey } = await response.json();
  
  // Subscribe
  subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(publicKey)
  });
  
  // Send subscription to server
  await saveSubscription(subscription);
  
  return subscription;
}

// Save subscription to server
async function saveSubscription(subscription) {
  const response = await fetch(`${API_URL}/api/push/subscribe`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeader()
    },
    body: JSON.stringify(subscription.toJSON())
  });
  
  if (!response.ok) {
    throw new Error('Failed to save subscription');
  }
  
  return response.json();
}

// Unsubscribe from push notifications
export async function unsubscribeFromPush() {
  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();
  
  if (subscription) {
    // Remove from server
    await fetch(`${API_URL}/api/push/unsubscribe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader()
      },
      body: JSON.stringify({ endpoint: subscription.endpoint })
    });
    
    // Unsubscribe locally
    await subscription.unsubscribe();
  }
}

// Get current subscription status
export async function getSubscriptionStatus() {
  if (!isPushSupported()) {
    return { supported: false, subscribed: false };
  }
  
  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();
  
  return {
    supported: true,
    permission: Notification.permission,
    subscribed: !!subscription
  };
}

// Register service worker
export async function registerServiceWorker() {
  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.register('/sw.js');
      console.log('Service Worker registered:', registration.scope);
      return registration;
    } catch (error) {
      console.error('Service Worker registration failed:', error);
      throw error;
    }
  }
}

// Show local notification (for testing)
export function showLocalNotification(title, options = {}) {
  if (Notification.permission === 'granted') {
    navigator.serviceWorker.ready.then((registration) => {
      registration.showNotification(title, {
        icon: '/icon-192.png',
        badge: '/icon-192.png',
        ...options
      });
    });
  }
}
